import React from 'react'
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  TableContainer,
  Typography,
  Box
} from '@mui/material'

// Shared column definition for longline-biology rows. Exported so the
// map-level "Export Displayed (CSV)" results bar can reuse it.
export const LONGLINE_BIOLOGY_COLUMNS = [
  { label: 'Station ID', key: 'SEAMAPSTATION' },
  { label: 'Date', key: 'SAMPLE_DATE' },
  { label: 'Genus', key: 'GENUS' },
  { label: 'Species', key: 'SPECIES' },
  { label: 'Bio Code', key: 'BIOCODE' },
  { label: 'Sex', key: 'SEX' },
  { label: 'PCL (mm)', key: 'PCL' },
  { label: 'SL (mm)', key: 'SL' },
  { label: 'FL (mm)', key: 'FL' },
  { label: 'TL (mm)', key: 'TL' },
  { label: 'DW (mm)', key: 'DW' },
  { label: 'Weight (kg)', key: 'WGT' },
  { label: 'Initial Condition', key: 'INITIALCOND' },
  { label: 'Release Condition', key: 'RELCOND' },
  { label: 'Fish ID', key: 'FISHID' }
]

// Normalize a raw row into export shape. Adds a combined `fullSpeciesName`
// when both genus and species are present so CSVs stay readable.
export const formatLonglineBiologyRowForExport = (row) => {
  const next = { ...row }
  if (next.GENUS && next.SPECIES && !next.fullSpeciesName) {
    next.fullSpeciesName = `${next.GENUS} ${next.SPECIES}`
  }
  return next
}

// `data` is the currently selected station's biology rows shown in the table.
// The per-point CSV button was removed in favor of the always-visible
// ResultsExportBar at the top of the table panel.
export default function PointDataTableLonglineBiology({ data }) {
  // If no data is selected yet, show nothing
  if (!data || !Array.isArray(data) || data.length === 0) {
    return null
  }

  const columns = LONGLINE_BIOLOGY_COLUMNS

  // Format dates if needed and create a display species name
  const formattedData = data.map(item => {
    // Create a copy of the item
    const newItem = { ...item }
    
    // Add a combined species name if both genus and species are available
    if (newItem.GENUS && newItem.SPECIES) {
      newItem.fullSpeciesName = `${newItem.GENUS} ${newItem.SPECIES}`
    }
    
    return newItem
  })

  return (
    <Box sx={{ 
      width: '100%', 
      paddingLeft: { xs: '0.5rem', sm: '1rem' },
      paddingRight: { xs: '0.5rem', sm: '1rem' }
    }}>
      <Box sx={{ marginBottom: { xs: 1, sm: 2 } }}>
        <Typography variant="h6" sx={{ 
          color: '#2E5A88', 
          fontSize: { xs: '0.9rem', sm: '1rem' } 
        }}>
          Bottom Longline Biology Details
        </Typography>
      </Box>
      <TableContainer 
        component={Paper} 
        elevation={2}
        sx={{ 
          width: '100%',
          overflowX: 'auto', // Enable horizontal scrolling
          overflowY: 'visible',
          maxHeight: 'none' // Remove height restrictions
        }}
      >
        <Table sx={{ 
          width: '100%',
          minWidth: { xs: '800px', md: '100%' } // Minimum width for mobile scrolling
        }}>
          <TableHead>
            <TableRow>
              {columns.map((col) => (
                <TableCell
                  key={col.key}
                  sx={{
                    fontWeight: 'bold',
                    backgroundColor: '#f5f5f5',
                    borderBottom: '2px solid #ccc',
                    color: '#1a2a3a',
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    padding: { xs: '8px', sm: '16px' },
                    whiteSpace: 'nowrap'
                  }}
                >
                  {col.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {formattedData.map((row, index) => (
              <TableRow 
                key={index}
                sx={{ '&:nth-of-type(odd)': { backgroundColor: 'grey.50' } }}
              >
                {columns.map((col) => {
                  let cellValue = row[col.key];
                  
                  // Format weight with one decimal place
                  if (['WGT'].includes(col.key)) {
                    if (!isNaN(cellValue) && cellValue !== null && cellValue !== '') {
                      cellValue = Number(cellValue).toFixed(1);
                    }
                  }
                  
                  // Format length measurements and disc width as whole numbers
                  if (['PCL', 'SL', 'FL', 'TL', 'DW'].includes(col.key)) {
                    if (!isNaN(cellValue) && cellValue !== null && cellValue !== '') {
                      cellValue = Math.round(Number(cellValue));
                    }
                  }
                  
                  return (
                    <TableCell 
                      key={`${index}-${col.key}`} 
                                            sx={{ 
                        color: index % 2 === 0 ? '#333333' : 'white',
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        padding: { xs: '8px', sm: '16px' },
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {cellValue}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}
